import { Accordion, Badge } from "react-bootstrap";
import Divider from "../../../components/Divider/Divider";
import {
    RequestBodyParameterArray,
    RequestBodyParameterBoolean,
    RequestBodyParameterEnum,
    RequestBodyParameterInteger,
    RequestBodyParameterNumber,
    RequestBodyParameterObject,
    RequestBodyParameterString,
    RequestBodyParameterUnsignedInteger,
    RequestHeadersParameterBoolean,
    RequestHeadersParameterEnum,
    RequestHeadersParameterInteger,
    RequestHeadersParameterNumber,
    RequestHeadersParameterString,
    RequestHeadersParameterUnsignedInteger,
    RequestPathParameterEnum,
    RequestPathParameterInteger,
    RequestPathParameterNumber,
    RequestPathParameterObject,
    RequestPathParameterString,
    RequestPathParameterUnsignedInteger,
    RequestQueryParameterBoolean,
    RequestQueryParameterEnum,
    RequestQueryParameterInteger,
    RequestQueryParameterNumber,
    RequestQueryParameterObject,
    RequestQueryParameterString,
    RequestQueryParameterUnsignedInteger,
    RouteDefinition
} from "smash-framework/dist/middlewares/apiGatewayProxyRest/interfaces";

interface Props {
    route: RouteDefinition;
}

type Parameter = RequestPathParameterString | RequestPathParameterObject | RequestPathParameterEnum | RequestPathParameterNumber | RequestPathParameterInteger | RequestPathParameterUnsignedInteger |
    RequestQueryParameterString | RequestQueryParameterEnum | RequestQueryParameterBoolean | RequestQueryParameterObject | RequestQueryParameterUnsignedInteger | RequestQueryParameterInteger | RequestQueryParameterNumber |
    RequestHeadersParameterUnsignedInteger | RequestHeadersParameterInteger | RequestHeadersParameterBoolean | RequestHeadersParameterNumber | RequestHeadersParameterString | RequestHeadersParameterEnum |
    RequestBodyParameterString | RequestBodyParameterEnum | RequestBodyParameterBoolean | RequestBodyParameterArray | RequestBodyParameterObject | RequestBodyParameterUnsignedInteger | RequestBodyParameterInteger | RequestBodyParameterNumber;

export const responseCode = {
    ok: 200,
    created: 201,
    accepted: 202,
    noContent: 204,
    partialContent: 206,
    movedPermanently: 301,
    found: 302,
    notModified: 304,
    temporaryRedirect: 307,
    permanentRedirect: 308,
    badRequest: 400,
    unauthorized: 401,
    forbidden: 403,
    notFound: 404,
    notAcceptable: 406,
    conflict: 409,
    unsupportedMediaType: 415,
    internalServerError: 500,
    notImplemented: 501,
    serviceUnavailable: 503,
    badGateway: 502,
    gatewayTimeout: 504,
}

const Endpoint = ({ route }: Props) => {

    const parseType = (parameter: {
        key: string;
        value: Parameter
    }) => {
        const { value } = parameter;
        if (!value) return null;

        const renderArrayContent = (content: any) => {
            if (content.type === 'object' && content.properties) {
                if (!Array.isArray(content.properties)) {
                    const entries = Object.entries(content.properties);
                    return (
                        <>
                            <span className="text-secondary">{value.type}</span>
                            <ul className="mb-0">
                                {entries.map(([objKey, property]) => (
                                    <li key={objKey}>
                                        {objKey} : {parseType({ key: objKey, value: property as Parameter })}
                                    </li>
                                ))}
                            </ul>
                        </>
                    );
                }
                return (
                    <>
                        <span className="text-secondary">{value.type}</span>
                        <ul className="mb-0">
                            {content.properties.map((p: any, idx: number) => (
                                <li key={idx}>
                                    {p.name} : {parseType({ key: p.name, value: p })}
                                </li>
                            ))}
                        </ul>
                    </>
                );
            }
            const displayType = `Array<${content.type}>`;
            return <span className="text-secondary">{displayType}</span>;
        };

        const renderProperties = (props: any) => {
            if (Object.keys(props).length === 0) return <span>{value.type}</span>;
            const entries = Object.entries(props);
            return (
                <>
                    <span className="text-secondary">{value.type}</span>
                    <ul className="mb-0">
                        {entries.map((entry) => {
                            const [objKey, objVal] = entry;
                            return (
                                <>
                                    <li key={objKey}>
                                        {objKey}: {parseType({ key: objKey, value: objVal as Parameter })}
                                    </li>
                                </>
                            )
                        }
                        )}
                    </ul>
                </>
            );
        };

        const renderEnum = (val: any) => {
            if (Array.isArray(val.values)) {
                return (
                    <span className="text-secondary">
                        {val.type} {val.values.map((v: any) =>
                            <Badge key={v} bg="secondary" className="mx-1">{v}</Badge>
                        )}
                    </span>
                );
            }
            if (val.values && typeof val.values === 'object') {
                const entries = Object.entries(val.values);
                return (
                    <span className="text-secondary">
                        {val.type} {entries.map(([k, v]) =>
                            <Badge key={k} bg="secondary" className="mx-1">{String(v)}</Badge>
                        )}
                    </span>
                );
            }
            return null;
        };

        switch (value.type) {
            case 'string':
            case 'number':
            case 'unsigned integer':
            case 'boolean':
                return <span className="text-secondary">{value.type}</span>;
            case 'array':
                return renderArrayContent(value.content);
            case 'enum':
                return renderEnum(value);
            case 'object':
                if (value.properties) {
                    if (!Array.isArray(value.properties)) {
                        return renderProperties(value.properties);
                    } else {
                        const entries = value.properties;
                        return (
                            <>
                                <span className="text-secondary">{value.type}</span>
                                <ul className="mb-0">
                                    {entries.map((p: any, idx: number) => (
                                        <li key={idx}>
                                            {p.name} : {parseType({ key: p.name, value: p })}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        );
                    }
                }
                return null;
            default:
                return null;
        }
    };

    const getPathParameters = (route: RouteDefinition) => {
        const props = route?.request?.pathParameters?.properties;
        if (!props) return null;
        return (
            <>
                <span className="fw-bold mb-2">Path parameters :</span>
                <div className="mt-2">
                    <code>
                        <ul className="mb-0">
                            {Object.entries(props).map(([key, val]) => (
                                <li key={key}>
                                    {key} {getRequiredOrOptional(val)} : {parseType({ key, value: val })}
                                </li>
                            ))}
                        </ul>
                    </code>
                </div>
            </>
        )
    };

    const getRequiredOrOptional = (val: any) => {
        if (val.optional) {
            return <span className="text-secondary">{'(optional)'}</span>;
        }
        return <span className="text-primary fw-bold">{'*required'}</span>;
    }

    const getDefaultValue = (val: any) => {
        if (val.default) {
            return <span className="text-secondary">{`(default: ${JSON.stringify(val.default)})`}</span>;
        }
        return null;
    }

    const getBodyParameters = (route: RouteDefinition) => {
        const props = route?.request?.body?.properties;
        if (!props) return null;

        return (
            <>
                <span className="fw-bold mb-2">Body parameters :</span>
                <div className="mt-2">
                    <code>
                        <ul className="mb-0">
                            {Object.entries(props).map(([key, val]) => (
                                <li key={key}>
                                    {key} {getRequiredOrOptional(val)} {getDefaultValue(val)}: {parseType({ key, value: val })}
                                </li>
                            ))}
                        </ul>
                    </code>
                </div>
            </>
        )
    };

    const getQueryParameters = (route: RouteDefinition) => {
        const props = route?.request?.queryParameters?.properties;
        if (!props) return null;
        return (
            <>
                <span className="fw-bold mb-2">Query parameters :</span>
                <div className="mt-2">
                    <code>
                        <ul className="mb-0">
                            {Object.entries(props).map(([key, val]) => (
                                <li key={key}>
                                    {key} : {parseType({ key, value: val })}
                                </li>
                            ))}
                        </ul>
                    </code>
                </div>
            </>
        );
    };

    const getErrorResponses = (route: RouteDefinition) => {
        const responses = route?.responses;
        if (!responses) return null;
        return Object.entries(responses).map(([respKey, response], idx) => {
            const code = responseCode[respKey as keyof typeof responseCode];
            if (code) {
                if (Array.isArray(response)) {
                    return (
                        <li key={idx}>
                            {code} : {response.map((resp, i) => (resp.name)).join(', ')}

                        </li>
                    );
                }
            }
            return null;
        });
    }

    const getResponses = (route: RouteDefinition) => {
        const responses = route?.responses;
        if (!responses) return null;
        return Object.entries(responses).map(([respKey, response], idx) => {
            const code = responseCode[respKey as keyof typeof responseCode];

            const renderPropertyList = (propsArr: any[]) => (
                <div>
                    <code>
                        <ul className="mb-0">
                            {propsArr.map((p: any, i: number) => (
                                <li key={i}>
                                    {p.name} : {parseType({ key: p.name, value: p.body ?? p })}
                                </li>
                            ))}
                        </ul>
                    </code>
                </div>
            );

            if (code) {
                if (!Array.isArray(response)) {
                    return (
                        <li key={idx}>
                            <span>{code}</span>
                            {renderPropertyList(response?.body?.properties || [])}
                        </li>
                    );
                }
            }
            return null;
        });
    };

    return (
        <Accordion defaultActiveKey="0" className="mb-2" key={route.action}>
            <Accordion.Item eventKey={route.request.path}>
                <Accordion.Header>
                    <span className="text-truncate">
                        {route.request.method} {route.request.path}
                    </span>
                </Accordion.Header>
                <Accordion.Body className="overflow-auto">
                    <span className="fw-bold">Action : </span><span>{route.action}</span>

                    <Divider />

                    <div>
                        {(route?.request?.pathParameters?.properties) && (
                            <>
                                {getPathParameters(route)}
                                <Divider />
                            </>
                        )}

                        {(route?.request?.queryParameters?.properties) && (
                            <>
                                {getQueryParameters(route)}
                                <Divider />
                            </>
                        )}
                        {(route?.request?.body?.properties) && (
                            <>
                                {getBodyParameters(route)}
                                <Divider />
                            </>
                        )}

                        {(!route?.request?.pathParameters?.properties &&
                            !route?.request?.queryParameters?.properties &&
                            !route?.request?.body?.properties) && (
                                <>
                                    <span>No parameters</span>
                                    <Divider />
                                </>
                            )}


                        <span className="fw-bold">Responses :</span>
                        <ul className="mb-0">{getResponses(route)}</ul>

                        <Divider />

                        <span className="fw-bold">Errors :</span>
                        <ul className="mb-0">{getErrorResponses(route)}</ul>
                    </div>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default Endpoint